import { isNetworkError, isAuthRejection } from '../utils/networkError';

/**
 * What a failed cold-start `GET /user` actually tells us about the session.
 *
 * `initializeAuth`'s catch used to take no parameter at all, so EVERY
 * rejection ran the same teardown — and that teardown calls
 * `tokenRefresher.clearTokens()`, which removes the REFRESH token too. There
 * is no way back from that except a full browser sign-in. A ten-second
 * timeout, a hotel captive portal, or one bad minute on the API therefore
 * signed the user out permanently, and at launch traffic that would have
 * happened to every open app at once.
 *
 * The rule this module encodes: destroying a session needs POSITIVE evidence
 * that the session is gone. Anything else keeps it. A wrongly-kept session
 * costs one more 401 on the next request (which the interceptors already
 * handle); a wrongly-destroyed one costs the user their whole local state.
 */

/** The server rejected the credentials — the session is genuinely over. */
export const AUTH_INIT_SIGNED_OUT = 'signed_out';
/** Nothing was reached. The session is untouched; the app is just offline. */
export const AUTH_INIT_OFFLINE = 'offline';
/** The server answered something else (5xx, a proxy's HTML, a parse error). */
export const AUTH_INIT_UNAVAILABLE = 'unavailable';

/**
 * Classify a `GET /user` rejection.
 *
 * Note there is no "throw from our own code" case here on purpose: post-
 * sign-in wiring (connectSocket, registerPlanListener,
 * syncLinkedAccountsFromIdp) must not be able to reach this function at all.
 * See store/sessionEffects.js — that separation is structural, not a matter
 * of classifying carefully.
 */
export function classifyAuthInitFailure(error) {
  if (isAuthRejection(error)) return AUTH_INIT_SIGNED_OUT;
  if (isNetworkError(error)) return AUTH_INIT_OFFLINE;
  // Everything else — a 500, a 502 from a proxy mid-deploy, a body that did
  // not parse — is the API having a bad moment, not proof of a dead session.
  // Keeping the tokens is the only recoverable answer: if the session really
  // is invalid, the very next request 401s and the interceptors tear it down
  // through the normal path.
  return AUTH_INIT_UNAVAILABLE;
}

/** True when this outcome justifies throwing the user's tokens away. */
export function clearsSession(outcome) {
  return outcome === AUTH_INIT_SIGNED_OUT;
}
